import {
  Children,
  isValidElement,
  useMemo,
  useState,
  type ComponentPropsWithoutRef,
  type ReactNode,
} from 'react';

import Spacings from '@dynatrace/strato-design-tokens/spacings';

import { DataTable } from '../../../tables/DataTable/DataTable.js';
import { DataTableActionsMenu } from '../../../tables/DataTable/features/UserActions/UserActionsMenu.js';
import type { DataTableDisplayColumnDef } from '../../../tables/DataTable/public.api.js';
import { getChildren, getTextAlignment } from '../../markdown/utils.js';

/**
 * Row shape produced by mapping tbody \> tr \> td — keyed by column index string.
 */
type AiResponseTableRow = Record<string, ReactNode[]>;

/**
 * Column definition for a markdown table. Cell values are the markdown nodes of a
 * single `td`, which `sortAccessor` flattens back to plain text to sort on.
 */
type AiResponseTableColumnDef = DataTableDisplayColumnDef<
  AiResponseTableRow,
  ReactNode
>;

/**
 * Recursively extracts plain text from a ReactNode for sorting.
 * @param node - The React node to extract text from.
 * @returns The concatenated plain-text content of the node.
 */
function extractText(node: ReactNode): string {
  if (typeof node === 'string' || typeof node === 'number') {
    return String(node);
  }
  if (isValidElement<{ children?: ReactNode }>(node)) {
    return extractText(node.props.children);
  }
  if (Array.isArray(node)) {
    return node.map(extractText).join('');
  }
  return '';
}

/**
 * Renders a markdown table using DataTable.
 * @internal
 */
export const AiResponseTable = ({
  children,
  style: consumerStyle,
  ...remainingProps
}: ComponentPropsWithoutRef<'table'>) => {
  const childrenArray = useMemo(() => Children.toArray(children), [children]);

  const currentHeaderKey = childrenArray.length
    ? extractText(childrenArray[0])
    : '';

  // `prevHeaderKey` lets us detect when the header text actually changes so we
  // can rebuild columns only then.
  const [prevHeaderKey, setPrevHeaderKey] = useState('');
  // `columns` is kept in state so DataTable receives a stable reference while
  // data rows stream in, which ensures smoother animation. If columns were derived
  // with useMemo, every new chunk would produce a new columns reference, causing
  // DataTable to reinitialize its internal state (sort order, column widths) on each update.
  const [columns, setColumns] = useState<AiResponseTableColumnDef[]>([]);

  if (currentHeaderKey !== prevHeaderKey) {
    setPrevHeaderKey(currentHeaderKey);
    if (childrenArray.length) {
      const tHeadRow = getChildren(childrenArray[0])[0];
      setColumns(
        getChildren(tHeadRow).map((cell, index): AiResponseTableColumnDef => ({
          header: () => (
            <DataTable.DefaultCell>{getChildren(cell)}</DataTable.DefaultCell>
          ),
          cell: (cell) => (
            <DataTable.DefaultCell>{cell.value}</DataTable.DefaultCell>
          ),
          accessor: String(index),
          // Cells reach sorting as plain text, so we use the numeric comparator.
          // It falls back to natural alphanumeric ordering for values that are
          // not plain numbers.
          sortType: 'number',
          sortAccessor: (row) => extractText(row[String(index)]),
          id: String(index),
          alignment: getTextAlignment(cell),
          width: 'content',
        })),
      );
    }
  }

  const data = useMemo<AiResponseTableRow[]>(() => {
    if (childrenArray.length < 2) {
      return [];
    }
    return getChildren(childrenArray[1]).map((row) =>
      Object.fromEntries(
        getChildren(row).map((cell, index): [string, ReactNode[]] => [
          String(index),
          getChildren(cell),
        ]),
      ),
    );
  }, [childrenArray]);

  return (
    <div
      style={{
        marginTop: Spacings.Size24,
        marginBottom: Spacings.Size24,
        ...consumerStyle,
      }}
      {...remainingProps}
    >
      <DataTable
        columns={columns}
        data={data}
        variant={{
          verticalDividers: true,
        }}
        sortable
        resizable
      >
        <DataTable.ColumnActions>
          <DataTableActionsMenu>
            <DataTableActionsMenu.LineWrap />
          </DataTableActionsMenu>
        </DataTable.ColumnActions>
      </DataTable>
    </div>
  );
};
